/* Atlas V3 Desktop — QoL kit: crossfades + editorial skeletons.
   House rules (do not genericize):
   - Skeletons mirror the EXACT geometry of what they replace — serif headline
     bars, mono kicker slivers, hairline rows with a logo circle — never a
     stack of uniform gray blobs. Shimmer is the warm-ink `.atlas-skeleton`
     gradient from src/v2/v3.css.
   - Motion is 200-240ms --studio-ease fade + ≤4px rise, nothing else. All of
     it behind the REDUCE guard. Skeletons are wordless.
   - Skeleton-flash guard: the skeleton's shimmer stays invisible for the
     first ~180ms (geometry still reserved), so cached loads swap straight to
     content without a flash — and without layout shift either way. */
import { useEffect, useState } from 'react'
import { REDUCE } from '@/v2/v3/lib'

const HAIR = '1px solid hsl(var(--studio-border) / 0.4)'

/* Swap skeleton → content with a single editorial fade. */
export function Crossfade({ loading, skeleton, children, style }) {
  const [showSkel, setShowSkel] = useState(false)
  useEffect(() => {
    if (!loading) { setShowSkel(false); return }
    const id = setTimeout(() => setShowSkel(true), 180)
    return () => clearTimeout(id)
  }, [loading])
  if (loading) {
    return <div style={{ opacity: showSkel ? 1 : 0, transition: 'opacity 160ms ease', ...style }}>{skeleton}</div>
  }
  return <div className="v3web-enter" style={style}>{children}</div>
}

/* Per-row entrance for freshly-loaded lists — 22ms stagger, capped at 10. */
export function enterStyle(i = 0) {
  if (REDUCE) return undefined
  return { animation: 'v3webEnter 220ms var(--studio-ease) backwards', animationDelay: `${Math.min(i, 10) * 22}ms` }
}

/* ── primitives ─────────────────────────────────────────── */
export function SkelText({ w = 120, h = 12, r = 3, style }) {
  return <span className="atlas-skeleton" style={{ display: 'block', width: w, height: h, borderRadius: r, flexShrink: 0, ...style }} />
}
export function SkelCircle({ size = 26 }) {
  return <span className="atlas-skeleton" style={{ display: 'block', width: size, height: size, borderRadius: '50%', flexShrink: 0 }} />
}

/* Matches .v3web-row: logo circle · name/sub bars · spark slot · numeric bar. */
export function SkelRowD({ i = 0, spark = true }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '13px 4px', borderTop: i ? HAIR : 'none' }}>
      <SkelCircle size={26} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
        <SkelText w={150} h={12} />
        <SkelText w={92} h={9} />
      </div>
      {spark && <SkelText w={56} h={20} r={4} />}
      <SkelText w={58} h={12} style={{ marginLeft: 4 }} />
    </div>
  )
}

/* Matches .v3web-disp: pip+time rail · meta row · serif headline · why line. */
export function SkelDisp({ i = 0 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '68px 1fr', gap: 16, padding: '16px 0', borderTop: i ? HAIR : 'none' }}>
      <div style={{ display: 'flex', gap: 10 }}>
        <span className="atlas-skeleton" style={{ width: 3, alignSelf: 'stretch', borderRadius: 2 }} />
        <SkelText w={44} h={9} style={{ marginTop: 3 }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SkelCircle size={20} />
          <SkelText w={46} h={11} />
        </div>
        <SkelText w="64%" h={16} />
        <SkelText w="38%" h={10} />
      </div>
    </div>
  )
}

/* Chart-shaped: shimmer area over a bottom hairline, not a floating blob. */
export function SkelChart({ height = 160 }) {
  return (
    <div style={{ width: '100%', height, display: 'flex', flexDirection: 'column' }}>
      <div className="atlas-skeleton" style={{ flex: 1, borderRadius: 'var(--studio-radius-sm)', opacity: 0.55 }} />
      <div style={{ height: 1, background: 'hsl(var(--studio-border) / 0.6)' }} />
    </div>
  )
}

/* Editorial block: mono kicker sliver + display-size serif bars + body line. */
export function SkelEditorial({ headline = 30, lines = 2 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <SkelText w={130} h={9} />
      <SkelText w="72%" h={headline} />
      {lines > 1 && <SkelText w="48%" h={headline} />}
      <SkelText w="88%" h={11} style={{ marginTop: 4 }} />
    </div>
  )
}
